import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { CheckSquare2, Clock, AlertTriangle, Download, Loader } from 'lucide-react';
import MyPendingTasksTable from './MyPendingTasksTable';
import MyCompletedTasksTable from './MyCompletedTasksTable';
import MyOverdueTasksTable from './MyOverdueTasksTable';
import MyUnderReviewTasksTable from './MyUnderReviewTasksTable';
import MyRejected from './MyRejected';
import { actionPointAPI, executeInBatches, userAPI } from '../../Services/apiService';
import { handleAPIError } from '../../utils/errorHandler';
import { showToast } from '../../utils/toast';
import { Calendar } from '../ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { format } from 'date-fns';

const MyTaskManagement = ({ subtab, taskId }) => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState(() => {
    if (subtab) return subtab;
    return 'overdue';
  });

  // Update activeTab if subtab prop changes
  useEffect(() => {
    if (subtab && subtab !== activeTab) {
      setActiveTab(subtab);
    }
  }, [subtab]);
  const [loading, setLoading] = useState(true);

  // Pagination states for each tab
  const [pendingPage, setPendingPage] = useState(1);
  const [completedPage, setCompletedPage] = useState(1);
  const [overduePage, setOverduePage] = useState(1);
  const [underReviewPage, setUnderReviewPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Separate state for each tab's tasks
  const [pendingTasks, setPendingTasks] = useState([]);
  const [completedTasks, setCompletedTasks] = useState([]);
  const [overdueTasks, setOverdueTasks] = useState([]);
  const [underReviewTasks, setUnderReviewTasks] = useState([]);

  // Pagination metadata for each tab
  const [pendingTotal, setPendingTotal] = useState(0);
  const [completedTotal, setCompletedTotal] = useState(0);
  const [overdueTotal, setOverdueTotal] = useState(0);
  const [underReviewTotal, setUnderReviewTotal] = useState(0);
  const [pendingPages, setPendingPages] = useState(1);
  const [completedPages, setCompletedPages] = useState(1);
  const [overduePages, setOverduePages] = useState(1);
  const [underReviewPages, setUnderReviewPages] = useState(1);
  const [exportDateRange, setExportDateRange] = useState({ from: null, to: null });
  const [loadingExport, setLoadingExport] = useState(false);

  // Counts from backend
  const [counts, setCounts] = useState({
    pending: 0,
    completed: 0,
    overdue: 0,
    submitted_for_review: 0,
    total: 0
  });

  const isInitialLoad = useRef(true);

  // Fetch tasks for a specific tab with pagination
  const fetchTasksByTab = async (tab, page) => {
    try {
      const response = await actionPointAPI.getMyActionPoints({
        tab,
        page,
        page_size: pageSize
      });

      console.log(`[${tab}] Backend response:`, response.data);

      if (response.status === 200 && response.data) {
        // Backend returns paginated response
        return {
          items: response.data.items || [],
          total: response.data.total || 0,
          pages: response.data.pages || 1,
          counts: response.data.counts || {}
        };
      }
      return null;
    } catch (error) {
      console.error(`Failed to fetch ${tab} tasks:`, error);
      return null;
    }
  };

  // Load counts for all tabs (for badges)
  useEffect(() => {
    const loadAllCounts = async () => {
      if (!user?.token) return;

      try {
        // OPTIMIZED: Execute in batches of 2 to reduce concurrent requests
        const [pendingResult, completedResult, overdueResult, underReviewResult] = await executeInBatches([
          () => fetchTasksByTab('pending', 1),
          () => fetchTasksByTab('completed', 1),
          () => fetchTasksByTab('overdue', 1),
          () => fetchTasksByTab('under_review', 1),
        ], 2);

        if (pendingResult) {
          setPendingTotal(pendingResult.total);
          setPendingPages(pendingResult.pages);
        }
        if (completedResult) {
          setCompletedTotal(completedResult.total);
          setCompletedPages(completedResult.pages);
        }
        if (overdueResult) {
          setOverdueTotal(overdueResult.total);
          setOverduePages(overdueResult.pages);
        }
        if (underReviewResult) {
          setUnderReviewTotal(underReviewResult.total);
          setUnderReviewPages(underReviewResult.pages);
        }

        // Update counts from any response (they're same in all)
        const anyCounts = pendingResult?.counts || completedResult?.counts || overdueResult?.counts || underReviewResult?.counts || {};
        console.log(anyCounts);
        setCounts({
          pending: anyCounts.pending || 0,
          completed: anyCounts.completed || 0,
          overdue: anyCounts.overdue || 0,
          submitted_for_review: anyCounts.submitted_for_review || 0,
          total: anyCounts.total || 0
        });
      } catch (error) {
        console.error('Failed to load task counts:', error);
      }
    };

    loadAllCounts();
  }, [user?.token, pageSize]);

  // Load tasks for active tab
  useEffect(() => {
    const loadTasks = async () => {
      if (!user?.token) {
        setLoading(false);
        return;
      }

      // Only show loading on initial load
      if (isInitialLoad.current) {
        setLoading(true);
        isInitialLoad.current = false;
      }

      try {
        let result;
        if (activeTab === 'pending') {
          result = await fetchTasksByTab('pending', pendingPage);
          if (result) {
            setPendingTasks(result.items);
            setPendingTotal(result.total);
            setPendingPages(result.pages);
          }
        } else if (activeTab === 'completed') {
          result = await fetchTasksByTab('completed', completedPage);
          if (result) {
            setCompletedTasks(result.items);
            setCompletedTotal(result.total);
            setCompletedPages(result.pages);
          }
        } else if (activeTab === 'overdue') {
          result = await fetchTasksByTab('overdue', overduePage);
          if (result) {
            setOverdueTasks(result.items);
            setOverdueTotal(result.total);
            setOverduePages(result.pages);
          }
        } else if (activeTab === 'under_review') {
          result = await fetchTasksByTab('under_review', underReviewPage);
          if (result) {
            setUnderReviewTasks(result.items);
            setUnderReviewTotal(result.total);
            setUnderReviewPages(result.pages);
          }
        }

        // Update counts
        if (result && result.counts) {
          setCounts({
            pending: result.counts.pending || 0,
            completed: result.counts.completed || 0,
            overdue: result.counts.overdue || 0,
            submitted_for_review: result.counts.submitted_for_review || 0,
            total: result.counts.total || 0
          });
        }
      } catch (error) {
        console.error('Failed to load tasks:', error);
      } finally {
        setLoading(false);
      }
    };

    loadTasks();
  }, [user?.token, activeTab, pendingPage, completedPage, overduePage, pageSize]);

  // Listen for task status changes and refresh
  // CRITICAL FIX: Always refresh ALL tabs when task status changes, not just active tab
  // This ensures data sync across all tabs (e.g., approved task appears in completed tab immediately)
  useEffect(() => {
    const handleTaskStatusChange = async () => {
      try {
        // CRITICAL: Always fetch current page for each tab to maintain pagination state
        // But update ALL tabs' data, not just active tab
        const [pendingResult, completedResult, overdueResult, underReviewResult] = await executeInBatches([
          () => fetchTasksByTab('pending', pendingPage),
          () => fetchTasksByTab('completed', completedPage),
          () => fetchTasksByTab('overdue', overduePage),
          () => fetchTasksByTab('under_review', underReviewPage),
        ], 2);

        // CRITICAL FIX: Update ALL tabs' data, not just active tab
        // This ensures when you approve a task, it immediately appears in completed tab
        if (pendingResult) {
          setPendingTotal(pendingResult.total);
          setPendingPages(pendingResult.pages);
          setPendingTasks(pendingResult.items); // Always update, not conditional
        }
        if (completedResult) {
          setCompletedTotal(completedResult.total);
          setCompletedPages(completedResult.pages);
          setCompletedTasks(completedResult.items); // Always update, not conditional
        }
        if (overdueResult) {
          setOverdueTotal(overdueResult.total);
          setOverduePages(overdueResult.pages);
          setOverdueTasks(overdueResult.items); // Always update, not conditional
        }
        if (underReviewResult) {
          setUnderReviewTotal(underReviewResult.total);
          setUnderReviewPages(underReviewResult.pages);
          setUnderReviewTasks(underReviewResult.items);
        }

        // Update counts from any response
        const anyCounts = pendingResult?.counts || completedResult?.counts || overdueResult?.counts || underReviewResult?.counts || {};
        setCounts({
          pending: anyCounts.pending || 0,
          completed: anyCounts.completed || 0,
          overdue: anyCounts.overdue || 0,
          submitted_for_review: anyCounts.submitted_for_review || 0,
          total: anyCounts.total || 0
        });
      } catch (error) {
        console.error('Failed to refresh tasks on status change:', error);
      }
    };

    window.addEventListener('taskStatusChanged', handleTaskStatusChange);

    return () => {
      window.removeEventListener('taskStatusChanged', handleTaskStatusChange);
    };
  }, [pendingPage, completedPage, overduePage, underReviewPage, pageSize]); // Removed activeTab dependency

  // Auto-refresh API calls every 30 seconds
  // CRITICAL FIX: Always refresh ALL tabs to ensure data sync
  useEffect(() => {
    if (!user?.token) return;

    const refreshData = async () => {
      try {
        // CRITICAL: Always fetch current page for each tab to maintain pagination
        // Update ALL tabs' data, not just active tab
        const [pendingResult, completedResult, overdueResult, underReviewResult] = await executeInBatches([
          () => fetchTasksByTab('pending', pendingPage),
          () => fetchTasksByTab('completed', completedPage),
          () => fetchTasksByTab('overdue', overduePage),
          () => fetchTasksByTab('under_review', underReviewPage),
        ], 2);

        // CRITICAL FIX: Update ALL tabs' data, not just active tab
        // This ensures data stays in sync across all tabs
        if (pendingResult) {
          setPendingTotal(pendingResult.total);
          setPendingPages(pendingResult.pages);
          setPendingTasks(pendingResult.items); // Always update
        }
        if (completedResult) {
          setCompletedTotal(completedResult.total);
          setCompletedPages(completedResult.pages);
          setCompletedTasks(completedResult.items); // Always update
        }
        if (overdueResult) {
          setOverdueTotal(overdueResult.total);
          setOverduePages(overdueResult.pages);
          setOverdueTasks(overdueResult.items); // Always update
        }
        if (underReviewResult) {
          setUnderReviewTotal(underReviewResult.total);
          setUnderReviewPages(underReviewResult.pages);
          setUnderReviewTasks(underReviewResult.items);
        }

        // Update counts
        const anyCounts = pendingResult?.counts || completedResult?.counts || overdueResult?.counts || underReviewResult?.counts || {};
        setCounts({
          pending: anyCounts.pending || 0,
          completed: anyCounts.completed || 0,
          overdue: anyCounts.overdue || 0,
          submitted_for_review: anyCounts.submitted_for_review || 0,
          total: anyCounts.total || 0
        });
      } catch (error) {
        console.error('Failed to refresh tasks:', error);
      }
    };

    // Set up interval to refresh every 30 seconds
    const intervalId = setInterval(refreshData, 30000);

    // Cleanup interval on unmount or when dependencies change
    return () => {
      clearInterval(intervalId);
    };
  }, [user?.token, pendingPage, completedPage, overduePage, underReviewPage, pageSize]); // Removed activeTab dependency

  // Handle page size change - reset all pages
  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setPendingPage(1);
    setCompletedPage(1);
    setOverduePage(1);
    setUnderReviewPage(1);
  };

  const handleExport = async () => {
    setLoadingExport(true);
    try {
      const params = {};
      if (exportDateRange?.from) params.start_date = format(exportDateRange.from, 'yyyy-MM-dd');
      if (exportDateRange?.to) params.end_date = format(exportDateRange.to, 'yyyy-MM-dd');
      const response = await userAPI.exporttask(params)
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Tasks_Export_${format(new Date(), 'yyyy-MM-dd')}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      showToast.success('Tasks exported successfully');

    } catch (error) {
      handleAPIError(error, 'Failed to export tasks');
    } finally {
      setLoadingExport(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="loading-shimmer h-8 w-64 rounded-lg"></div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[1, 2, 3].map(i => (
            <div key={i} className="loading-shimmer h-32 rounded-xl"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Task Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        {/* <TabsList className="grid grid-cols-4 p-0"> */}
        <TabsList className="grid grid-cols-2 sm:grid-cols-4 gap-1 p-0 h-auto">
          <TabsTrigger value="overdue" className="flex flex-wrap justify-center gap-1 text-xs sm:text-sm px-2 py-2">
            <AlertTriangle className="h-4 w-4" />
            Overdue
            {counts.overdue > 0 && (
              <Badge variant="destructive" className="ml-1">
                {counts.overdue}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="pending" className="flex flex-wrap justify-center gap-1 text-xs sm:text-sm px-2 py-2">
            <Clock className="h-4 w-4" />
            Pending
            {counts.pending > 0 && (
              <Badge variant="secondary" className="ml-1 bg-yellow-200">
                {counts.pending}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="under_review" className="flex flex-wrap justify-center gap-1 text-xs sm:text-sm px-2 py-2">
            <Clock className="h-4 w-4" />
            Under Review
            {counts.submitted_for_review > 0 && (
              <Badge variant="secondary" className="ml-1 bg-orange-200">
                {counts.submitted_for_review}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="completed" className="flex flex-wrap justify-center gap-1 text-xs sm:text-sm px-2 py-2">
            <CheckSquare2 className="h-4 w-4" />
            Completed
            {counts.completed > 0 && (
              <Badge variant="secondary" className="ml-1 bg-green-300">
                {counts.completed}
              </Badge>
            )}
          </TabsTrigger>


        </TabsList>
        <div className="flex gap-2 justify-end">
          <Popover>
            <PopoverTrigger asChild>
              <button className="flex flex-wrap justify-center items-center border border-gray-200 gap-1 text-xs sm:text-sm px-2 py-2 rounded-md" style={{ background: 'linear-gradient(135deg, var(--primary-gold), var(--hover-gold))', color: 'white' }}>
                <Download className="h-3 w-3" />
                Export Task
              </button>
            </PopoverTrigger>
            <PopoverContent
              className="w-[80vw]  sm:w-72 p-0 overflow-hidden shadow-2xl border border-gray-100 z-50"
              align="end"
              side="bottom"
              sideOffset={8}
            >
              <div className="p-3 sm:p-4 space-y-2">
                <h4 className="font-semibold text-sm sm:text-base text-blue-900">Export Date Range</h4>
                <p className="text-[10px] sm:text-[11px] text-gray-500 leading-tight">Select the period you want to export Task for.</p>
                <p className="text-[9px] sm:text-[10px] text-red-500 font-medium italic">* You can only get the data of max 90days</p>

                <div className="flex justify-center pt-1">
                  <Calendar
                    mode="range"
                    defaultMonth={exportDateRange?.from}
                    selected={exportDateRange}
                    onSelect={setExportDateRange}
                    numberOfMonths={1}
                    className="rounded-md border p-0.5 sm:p-1"
                  />
                </div>
              </div>

              <div className="bg-blue-50/80 p-3 mt-auto flex justify-between items-center border-t border-blue-100">
                <div className="flex flex-col">
                  <span className="text-[9px] text-blue-600 uppercase font-bold tracking-wider">Selection</span>
                  <span className="text-xs font-bold text-blue-900">
                    {exportDateRange?.from ? (
                      exportDateRange.to ? (
                        `${format(exportDateRange.from, "MMM dd")} - ${format(exportDateRange.to, "MMM dd")}`
                      ) : (
                        format(exportDateRange.from, "MMM dd, yyyy")
                      )
                    ) : (
                      "Entire History"
                    )}
                  </span>
                </div>
                <button
                  onClick={handleExport}
                  className="bg-amber-500 hover:bg-amber-600 text-white shadow-sm flex gap-2 px-3 py-2 cursor-pointer text-[11px] font-bold rounded-md items-center transition-all"
                  style={{ background: '#f59e0b', cursor: loadingExport ? 'not-allowed' : 'pointer' }}
                  disabled={loadingExport}
                >
                  {loadingExport ? (
                    <Loader className="w-3.5 h-3.5" />
                  ) : (
                    <Download className="w-3.5 h-3.5" />
                  )}
                  Download XLSX
                </button>
              </div>
            </PopoverContent>
          </Popover>
        
        </div>

        <TabsContent value="pending">
          <MyPendingTasksTable
            tasks={pendingTasks}
            page={pendingPage}
            pages={pendingPages}
            total={pendingTotal}
            pageSize={pageSize}
            hasNext={pendingPage < pendingPages}
            hasPrev={pendingPage > 1}
            onPageChange={setPendingPage}
            onPageSizeChange={handlePageSizeChange}
          />
        </TabsContent>

        <TabsContent value="under_review">
          <MyUnderReviewTasksTable
            tasks={underReviewTasks}
            page={underReviewPage}
            pages={underReviewPages}
            total={underReviewTotal}
            pageSize={pageSize}
            hasNext={underReviewPage < underReviewPages}
            hasPrev={underReviewPage > 1}
            onPageChange={setUnderReviewPage}
            onPageSizeChange={handlePageSizeChange}
          />
        </TabsContent>

        <TabsContent value="completed">
          <MyCompletedTasksTable
            tasks={completedTasks}
            page={completedPage}
            pages={completedPages}
            total={completedTotal}
            pageSize={pageSize}
            hasNext={completedPage < completedPages}
            hasPrev={completedPage > 1}
            onPageChange={setCompletedPage}
            onPageSizeChange={handlePageSizeChange}
          />
        </TabsContent>

        <TabsContent value="overdue">
          <MyOverdueTasksTable
            tasks={overdueTasks}
            page={overduePage}
            pages={overduePages}
            total={overdueTotal}
            pageSize={pageSize}
            hasNext={overduePage < overduePages}
            hasPrev={overduePage > 1}
            onPageChange={setOverduePage}
            onPageSizeChange={handlePageSizeChange}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default MyTaskManagement;